import { Link, useSearchParams } from 'react-router-dom';
import {
  Logo, ArrowRight, FileText, Calendar, GraduationCap, Users, Stethoscope, Brain, Shield, Eye, Sparkles, Phone, BarChart
} from '../components/Icons.jsx';
import FounderNote from '../components/FounderNote.jsx';
import { useAuth } from '../lib/auth.jsx';

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'cohort', label: 'My cohort' },
  { id: 'classroom', label: 'Classroom resources' },
  { id: 'onboard', label: 'Onboard your school' }
];

export default function Teachers() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const { user } = useAuth();
  const setTab = (id) => setParams(id === 'overview' ? {} : { tab: id }, { replace: true });

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <header style={{
        background: '#fff', borderBottom: '1px solid var(--border)',
        padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10
      }}>
        <Link to="/teachers" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Logo width="36" height="36" onLight />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi</span>
            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 4 }}>For teachers · powered by Medic Mind</span>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {user ? (
            <>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Signed in as {user.email}</span>
              <Link to="/" className="btn btn-secondary">Open portal →</Link>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-secondary" style={{ textDecoration: 'none' }}>Sign in</Link>
              <Link to="/signup?role=teacher" className="btn btn-primary" style={{ textDecoration: 'none' }}>Create teacher account →</Link>
            </>
          )}
        </div>
      </header>

      <main className="main" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <div className="welcome-kicker">For teachers, UCAS advisors &amp; SENCos</div>
          <h1 className="welcome-h1">{user?.email ? `Hi ${(user.email || '').split('@')[0]} 👋` : 'Give every medic in your sixth form the same shot.'}</h1>
          <p className="welcome-sub">Free for schools. The portal designed by a UCAT 100% scorer, plus a partnership track if you want bulk access.</p>
        </div>

        <TabBar tabs={tabs} active={tab} onChange={setTab} />

        {tab === 'overview' && <Overview />}
        {tab === 'cohort' && <MyCohort />}
        {tab === 'classroom' && <Classroom />}
        {tab === 'onboard' && <Onboard />}

        <footer style={{ textAlign: 'center', padding: '40px 0 12px', color: 'var(--text-muted)', fontSize: 13 }}>
          © {new Date().getFullYear()} Medic Mind. UCAT is a registered trademark of UCAT Consortium. We are not affiliated with the consortium.
        </footer>
      </main>
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <nav style={{ display: 'flex', gap: 4, padding: 4, marginBottom: 22, background: 'var(--card-soft)', borderRadius: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      {tabs.map((t) => (
        <button
          key={t.id} onClick={() => onChange(t.id)}
          style={{
            padding: '8px 14px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
            background: active === t.id ? '#fff' : 'transparent',
            color: active === t.id ? 'var(--brand-deep)' : 'var(--text-muted)',
            border: 'none', borderRadius: 8, fontFamily: 'inherit', whiteSpace: 'nowrap',
            boxShadow: active === t.id ? 'var(--shadow-card)' : 'none'
          }}
        >{t.label}</button>
      ))}
    </nav>
  );
}

function Overview() {
  return (
    <>
      <FounderNote
        kicker="A note from"
        title="To the staff supporting medics."
        ctaText="Read the full letter"
        ctaTo="/about"
      >
        <p style={{ marginTop: 0 }}>You're spread thin. You're not a UCAT expert. And every year you've got a handful of medics in your sixth form who need more than the school can give — including the ones who'd never afford a tutor.</p>
        <p>This is what we built for you. The whole portal is free. 13 drills, the personal-statement analyser, the AI tutor (Ash), Career Camp — everything your students would otherwise have to pay for elsewhere.</p>
        <p>60,000+ medical and dental applicants have come through Medic Mind in 10 years. Their average UCAT lands in the top 30% nationally because every tutor was personally trained by me on the playbooks now living in this portal.</p>
        <p>Onboard your school in 5 minutes via the partnership track. We'll generate sign-up links, set up your reporting view, and discount Career Camp places for partner schools.</p>
        <p>If even one of your students gets an offer they wouldn't have got otherwise, this is worth your 15 minutes.</p>
      </FounderNote>

      <section className="r-grid-3" style={{ marginBottom: 22 }}>
        <Metric value="Free" label="The student-facing portal — always, no per-seat licensing" />
        <Metric value="60,000+" label="applicants mentored to doctors over 10 years by Medic Mind" />
        <Metric value="Top 30%" label="UCAT average of Medic Mind tutees nationally" />
      </section>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>The essentials</h2>
      <section className="parents-grid" style={{ marginBottom: 28 }}>
        <TeacherCard icon={<FileText />} title="What the UCAT is" body="2-hour test. Three reasoning sections scored 300–900 each (max 2700). SJT banded 1–4. 2024 dropped Abstract Reasoning." />
        <TeacherCard icon={<BarChart />} title="What a 'good' score means" body="Median ~2400. Russell Group typically interview from 2500–2700+. Cambridge / Oxford typically clear 2700." />
        <TeacherCard icon={<GraduationCap />} title="Subject + grade rules" body="Chemistry required everywhere. Biology required at most schools. Typical offers A*AA–AAA, IB 36–40." />
        <TeacherCard icon={<Users />} title="Spotting a viable applicant" body="Strong GCSE profile (mostly 7+), Chem + Bio at A-level, motivation grounded in real work experience, resilience for rejection." />
        <TeacherCard icon={<Stethoscope />} title="Work experience guidance" body="One Career Camp week + ongoing volunteering beats six one-off shadowing days." />
        <TeacherCard icon={<Brain />} title="When to recommend a tutor" body="Plateaus despite consistent prep, one section dragging the total, &lt; 6 weeks out with no plan." />
      </section>
    </>
  );
}

function MyCohort() {
  return (
    <>
      <section className="card" style={{ marginBottom: 22, padding: '24px 26px' }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 20 }}>Your cohort dashboard</h2>
        <p style={{ color: 'var(--text-muted)', margin: '0 0 18px', fontSize: 14 }}>
          Once you've onboarded your school, this panel will show every connected student's drill progress, plateau patterns, and which of them are ready for additional support. Currently being wired up to the Supabase backend — your partnership call will set this up.
        </p>

        <div className="r-grid-3" style={{ marginBottom: 16 }}>
          <Metric value="—" label="Students connected" />
          <Metric value="—" label="Average UCAT (cohort)" />
          <Metric value="—" label="Students plateauing" />
        </div>

        <Link to="/for-schools" className="btn btn-primary">Onboard your school <ArrowRight width="14" height="14" /></Link>
      </section>

      <section className="card" style={{ marginBottom: 22, padding: '22px 24px', background: 'linear-gradient(135deg, var(--accent-soft), #fff)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Until then — the manual playbook</h3>
        <p style={{ margin: '0 0 12px', color: 'var(--text-2)', fontSize: 14 }}>What to do in your 1-to-1s with applicants while the cohort dashboard ships:</p>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.7 }}>
          <li>Run <Link to="/grade-check" style={{ color: 'var(--accent)', fontWeight: 600 }}>Grade check</Link> together — sets expectations on which schools their predicted grades reach.</li>
          <li>Run <Link to="/where-can-i-apply" style={{ color: 'var(--accent)', fontWeight: 600 }}>Where can I apply</Link> together after their first UCAT mock — turns a number into a shortlist.</li>
          <li>For students plateauing on a section, point them at the matching drill (<Link to="/practice" style={{ color: 'var(--accent)', fontWeight: 600 }}>Practice page</Link>). End-of-drill tutor nudges fire automatically when accuracy is below 65%.</li>
          <li>For widening-participation eligible students, flag Career Camp bursaries (50% / 75% / 100%) — the single biggest equaliser.</li>
        </ul>
      </section>
    </>
  );
}

function Classroom() {
  return (
    <>
      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Free classroom resources</h2>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 16px', maxWidth: 720 }}>Project these in lessons or send the links home. No login required to use them.</p>
      <section className="parents-grid" style={{ marginBottom: 28 }}>
        <ResourceCard to="/videos" icon={<Eye />} title="UCAT free videos" body="17 short Medic Mind YouTube teaching videos. Project in PSHE or send for revision week." />
        <ResourceCard to="/practice" icon={<FileText />} title="13 free drills" body="Every drill has Easy / Medium / Hard modes plus UCATSEN time bonus." />
        <ResourceCard to="/tutor-portal" icon={<Sparkles />} title="Tutor portal (passcode-gated)" body="Live lesson tools designed for projector / Zoom. Hidden-answer scenarios, facilitator notes only you see." />
        <ResourceCard to="/career-camp" icon={<Stethoscope />} title="Career Camp" body="In-person NHS hospital + hospice placements. Bursaries up to 100% — share with eligible cohorts." />
        <ResourceCard to="/grade-check" icon={<GraduationCap />} title="Grade realism check" body="30-second tool — A-level / IB vs entry bars at all 28 UK med schools." />
        <ResourceCard to="/timeline" icon={<Calendar />} title="Application timeline" body="Year-by-year milestones. Print as a handout or stick it to the wall." />
      </section>

      <h2 style={{ margin: '32px 0 14px', fontSize: 22 }}>Term-by-term checklist</h2>
      <section className="card" style={{ marginBottom: 22 }}>
        <div className="timeline">
          <TimelineRow when="Year 11/12 — Autumn" what="Identify medics early" body="Strong sciences + GCSE 7s+ is the threshold signal. Make sure they pick Chemistry at A-level." />
          <TimelineRow when="Year 12 — Spring" what="Work experience kick-off" body="At least one in-person clinical placement booked for the summer. Career Camp London cohort fills early." />
          <TimelineRow when="Year 12 — Summer" what="UCAT taster" body="2-week light exposure + one timed paper to find weak sections without test-date pressure." />
          <TimelineRow when="Year 13 — June" what="Predicted-grade conversations" body="UCAS predictions go out in autumn but students need to know now." />
          <TimelineRow when="Year 13 — Jul–Sep" what="UCAT testing" body="Encourage early booking. Late August / September slots fill fast." />
          <TimelineRow when="Year 13 — September" what="Reference + PS edits" body="Reference should mention school-facilitated work experience, evidence of motivation, how they handled adversity." />
          <TimelineRow when="Year 13 — Oct 15" what="UCAS deadline" body="Hard deadline. Run a 1 Oct internal deadline." />
          <TimelineRow when="Year 13 — Nov–Mar" what="Interview prep" body="In-school MMI mocks make a measurable difference." />
        </div>
      </section>
    </>
  );
}

function Onboard() {
  return (
    <>
      <section style={{ background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))', color: '#fff', borderRadius: 18, padding: 28, marginBottom: 22 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.85, marginBottom: 6 }}>School partnership</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 800, letterSpacing: '-0.01em' }}>Onboard your school in 5 minutes.</h2>
        <p style={{ margin: '0 0 16px', color: '#e9d5ff', fontSize: 14.5, maxWidth: 580 }}>Free for schools. Bulk sign-up links for your cohort, a reporting view for staff, and discounted Career Camp + crash-course visits for partner schools. No per-seat licensing.</p>
        <Link to="/for-schools" className="btn" style={{ background: '#fff', color: 'var(--brand-deep)' }}>Open the partnership LP <ArrowRight width="14" height="14" /></Link>
      </section>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>How onboarding works</h2>
      <div className="r-grid-3" style={{ marginBottom: 24 }}>
        <Step n="1" title="Send the enquiry form" body='On the partnership LP. School name, your details, rough cohort size. We reply within 1 working day.' />
        <Step n="2" title="Bulk-add your cohort" body="We generate sign-up links you send round in one email. Students claim accounts in 30 seconds." />
        <Step n="3" title="Partnership call" body="15-min Zoom: walk through the platform, set up reporting, scope Career Camp / in-person crash-course options." />
      </div>

      <section className="card" style={{ marginBottom: 22, borderLeft: '5px solid var(--brand-teal)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 18 }}>Free for schools. Always.</h3>
        <p style={{ margin: 0, color: 'var(--text-2)' }}>The student-facing portal is and will remain free. Career Camp / 1-to-1 tutoring / crash-course visits are paid — discounted for partner schools, with bursaries up to 100% for widening-participation students.</p>
      </section>

      <section className="parents-cta">
        <div>
          <h2>Got a specific question?</h2>
          <p>Free 15-min partnership call. No salesy follow-up.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free call <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

function Metric({ value, label }) {
  return (
    <div className="card" style={{ borderLeft: '4px solid var(--brand-purple)' }}>
      <div style={{ fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', color: 'var(--brand-deep)', letterSpacing: '-0.01em' }}>{value}</div>
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-2)' }}>{label}</p>
    </div>
  );
}
function TeacherCard({ icon, title, body }) {
  return <div className="parents-card"><div className="icon-wrap">{icon}</div><h3>{title}</h3><p>{body}</p></div>;
}
function ResourceCard({ to, icon, title, body }) {
  return (
    <Link to={to} className="parents-card" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <div className="icon-wrap">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
      <span style={{ marginTop: 10, color: 'var(--accent)', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>Open <ArrowRight width="14" height="14" /></span>
    </Link>
  );
}
function Step({ n, title, body }) {
  return (
    <div className="parents-card" style={{ borderLeft: '4px solid var(--brand-purple)' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--brand-purple)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', fontSize: 17, marginBottom: 10 }}>{n}</div>
      <h3 style={{ fontSize: 16 }}>{title}</h3>
      <p>{body}</p>
    </div>
  );
}
function TimelineRow({ when, what, body }) {
  return <div className="timeline-row"><div className="when">{when}</div><div className="what"><h4>{what}</h4><p>{body}</p></div></div>;
}
